import { memo } from 'react'
import TemplateSettingsSheet from 'builder/components/TemplateSettingsSheet'
import { isResumeDocument } from 'builder/utils/getDocumentType'
import {
  CustomizationButton,
  DownloadButton,
  EditorMobileFooter,
} from 'builder/components/PreviewPanel'
import { EditorPanelSwitcher } from 'builder/components/EditorPanelSwitcher'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors } from 'builder/modules/resumeEditor'
import {
  StartTailoringButton,
  getAutotailoringHeaderHeight,
} from 'builder/components/DocumentEditor/TailoringFromPublicJobBoard'
import { useScrollState } from 'builder/hooks/useScrollState'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { TemplateCustomizationSheet } from '../TemplateCustomizationSheet'
import { DocumentCustomizationModalProps } from '../../types'
import { CUSTOMIZATION_MODAL_TEST_ID } from '../../constants'
import {
  Container,
  EditorPanelSwitcherWrapper,
  StyledTailoringFromPublicJobBoardHeader,
} from './styles'
import { useDocumentPreview } from './useDocumentPreview'
import PreviewContent from './PreviewContent'

export const MobileDocumentCustomizationModal = memo((props: DocumentCustomizationModalProps) => {
  const {
    updateTemplate,
    currentDocument,
    type,
    showResumeDistributionModal,
    handleFontChange,
    handleChangeLayoutFormat,
    handleColorSelect,
    handleSpacingChange,
    handleSizeChange,
    renderPreview,
    handleDownloadDocument,
    editorPanelType,
    handleEditorPanelTypeChange,
    handleShareDocument,
    handleCombineDocuments,
    mobilePanelSwitcherItems,
    handleSettingsFieldChange,
  } = props

  const isResume = isResumeDocument(currentDocument)

  const {
    currentPage,
    totalPagesNumber,
    isSheetVisible,
    width,
    height,
    contentRef,
    previewBoxRef,
    previewRef,
    handlePreviewResize,
    handlePaginationStateChange,
    handlePreviousPageClick,
    handleNextPageClick,
    handleSheetOpen,
    handleSheetClose,
    isDataLoaded,
    templates,
    currentTemplate,
    colorSettings,
  } = useDocumentPreview(props)

  const autoTailoringFromPublicJobBoard = useTypedSelector(
    selectors.autoTailoringFromPublicJobBoard,
  )

  const { canScrollTop, canScrollBottom } = useScrollState({ elementRef: contentRef })
  const mediaQueries = useMediaQueries()
  const autotailoringHeaderHeight = autoTailoringFromPublicJobBoard.enabled
    ? getAutotailoringHeaderHeight(mediaQueries)
    : 0

  return (
    <Container data-testid={CUSTOMIZATION_MODAL_TEST_ID}>
      {autoTailoringFromPublicJobBoard.enabled && <StyledTailoringFromPublicJobBoardHeader />}

      <EditorPanelSwitcherWrapper
        $isHeaderShadowVisible={canScrollTop}
        $stickyTop={autotailoringHeaderHeight}
      >
        <EditorPanelSwitcher
          isMobileModal={true}
          activePanelType={editorPanelType}
          handlePanelTypeChange={handleEditorPanelTypeChange}
          items={mobilePanelSwitcherItems}
        />
      </EditorPanelSwitcherWrapper>

      <PreviewContent
        {...{
          contentRef,
          previewBoxRef,
          previewRef,
          type,
          width,
          height,
          isDataLoaded,
          currentDocument,
          currentTemplate,
          currentPage,
          totalPagesNumber,
          handlePreviousPageClick,
          handleNextPageClick,
          handlePaginationStateChange,
          handlePreviewResize,
          renderPreview,
        }}
      />

      <EditorMobileFooter isFooterShadowVisible={canScrollBottom}>
        {autoTailoringFromPublicJobBoard.enabled ? (
          <StartTailoringButton />
        ) : (
          <>
            <CustomizationButton onClick={handleSheetOpen} />
            <DownloadButton
              handleDownloadDocument={handleDownloadDocument}
              handleShareDocument={handleShareDocument}
              handleCombineDocuments={handleCombineDocuments}
              showResumeDistributionModal={showResumeDistributionModal}
              shouldShowIcon={false}
            />
          </>
        )}
      </EditorMobileFooter>

      {isDataLoaded &&
        isSheetVisible &&
        (isResume ? (
          <TemplateCustomizationSheet
            {...{
              resume: currentDocument,
              updateTemplate,
              onClose: handleSheetClose,
              handleFontChange,
              handleChangeLayoutFormat,
              handleColorSelect,
              handleSpacingChange,
              handleSizeChange,
              handleSettingsFieldChange,
            }}
          />
        ) : (
          <TemplateSettingsSheet
            type={type}
            isSpacingSupported={currentTemplate?.supportsSpacing}
            templates={templates}
            selectedTemplate={currentDocument.template}
            onColorChange={handleColorSelect}
            onTemplateChange={updateTemplate}
            onSpacingChange={handleSpacingChange}
            onClose={handleSheetClose}
            {...colorSettings}
          />
        ))}
    </Container>
  )
})
